import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { site } from "@/data/site";

export const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();
    const { pathname } = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (href) => {
        setOpen(false);
        if (pathname === "/") {
            document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
        } else {
            navigate(`/${href}`);
        }
    };

    return (
        <>
            <motion.header
                data-testid="navbar"
                initial={{ y: -80, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
                className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,backdrop-filter,border-color,box-shadow] duration-500 ${
                    scrolled
                        ? "border-b border-line bg-ivory/85 backdrop-blur-xl shadow-[0_1px_30px_rgba(43,43,43,0.05)]"
                        : "border-b border-transparent bg-transparent"
                }`}
            >
                <nav className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-4 lg:px-10">
                    <button
                        data-testid="nav-logo"
                        onClick={() => (pathname === "/" ? go("#top") : navigate("/"))}
                        className="flex items-center gap-3"
                        aria-label="Swastik Realty home"
                    >
                        <img src={site.logo} alt="Swastik Realty" className="h-12 w-12 object-contain" />
                        <span className="hidden flex-col leading-none sm:flex">
                            <span className="font-serif text-lg tracking-wide text-charcoal">
                                Swastik Realty
                            </span>
                            <span className="mt-1 text-[9px] uppercase tracking-luxe text-orange">
                                Find | Trust | Grow
                            </span>
                        </span>
                    </button>

                    <div className="hidden items-center gap-9 lg:flex">
                        {site.nav.map((n) => (
                            <button
                                key={n.href}
                                data-testid={`nav-link-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                                onClick={() => go(n.href)}
                                className="group relative text-xs uppercase tracking-[0.15em] text-charcoal/80 transition-colors duration-300 hover:text-orange"
                            >
                                {n.label}
                                <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-orange transition-all duration-300 group-hover:w-full" />
                            </button>
                        ))}
                    </div>

                    <div className="flex items-center gap-3">
                        <button
                            data-testid="nav-consultation-button"
                            onClick={() => go("#consultation")}
                            className="hidden items-center bg-orange px-6 py-2.5 text-xs uppercase tracking-[0.15em] text-white transition-colors duration-300 hover:bg-orange-dark sm:flex"
                        >
                            Book Consultation
                        </button>
                        <button
                            data-testid="nav-menu-toggle"
                            onClick={() => setOpen((o) => !o)}
                            className="flex h-11 w-11 items-center justify-center border border-line text-charcoal lg:hidden"
                            aria-label="Toggle menu"
                        >
                            {open ? <X size={18} /> : <Menu size={18} />}
                        </button>
                    </div>
                </nav>
            </motion.header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="mobile-menu"
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                        className="fixed inset-x-0 top-[76px] z-40 border-b border-line bg-ivory/95 backdrop-blur-xl lg:hidden"
                    >
                        <div className="flex flex-col px-6 py-6">
                            {site.nav.map((n) => (
                                <button
                                    key={n.href}
                                    onClick={() => go(n.href)}
                                    className="border-b border-line py-4 text-left font-serif text-2xl text-charcoal"
                                >
                                    {n.label}
                                </button>
                            ))}
                            <button
                                onClick={() => go("#consultation")}
                                className="mt-6 bg-orange px-6 py-3 text-xs uppercase tracking-[0.15em] text-white"
                            >
                                Book Consultation
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};
